import { FileWarning, ScanSearch, TriangleAlert, type LucideIcon } from "lucide-react"

import { cn } from "@/lib/utils"

import type { ScopeNotice as ScopeNoticeItem } from "./report-data"

interface NoticePresentation {
  heading: string
  Icon: LucideIcon
}

const PRESENTATION: Record<ScopeNoticeItem["kind"], NoticePresentation> = {
  unreadable: { heading: "This label could not be reviewed automatically", Icon: FileWarning },
  // Not "some pages could not be read": the sentence after it opens by naming
  // them, and it also covers a passage two readers read differently.
  ocr: { heading: "Some of this label was not read reliably", Icon: ScanSearch },
  unavailable: { heading: "This review is incomplete", Icon: TriangleAlert },
}

/**
 * What the report has to say about its own run, wherever the reader landed.
 *
 * Above the tabs rather than inside one, as on the legacy console: `?view=` is
 * a URL parameter, so a client can be sent straight to the Checklist and work a
 * findings list that is short for a reason they were never told. Renders
 * nothing for a complete run.
 *
 * In the "in progress" amber rather than the error red: none of these says the
 * label is wrong, only that the review could not see all of it, and red here
 * would compete with the findings that do say something is wrong.
 */
export function ScopeNotice({
  notices,
}: {
  notices: readonly ScopeNoticeItem[]
}): JSX.Element | null {
  if (notices.length === 0) return null
  return (
    // A named landmark, so a screen-reader user linked straight to a tab can
    // find it, rather than meeting an unlabelled run of paragraphs.
    <aside
      aria-label="About this review"
      className="mx-auto mt-8 max-w-[760px] overflow-hidden rounded-lg border border-print/30 bg-print-tint"
    >
      <ul className="divide-y divide-print/20">
        {notices.map((notice) => {
          const { heading, Icon } = PRESENTATION[notice.kind]
          return (
            <li key={notice.kind} className="flex items-start gap-3 px-4 py-3.5">
              <span
                aria-hidden="true"
                className={cn(
                  "mt-0.5 grid size-8 shrink-0 place-items-center rounded-full",
                  "bg-surface text-print ring-1 ring-print/25",
                )}
              >
                <Icon className="size-4" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-body-sm font-semibold text-print-strong">{heading}</p>
                <p className="mt-0.5 text-body-sm text-text-2">{notice.text}</p>
              </div>
            </li>
          )
        })}
      </ul>
    </aside>
  )
}
